"use client";
import { useEffect, useRef } from "react";

type Props = {
    src: string;
    poster?: string;
    aspectRatio?: string;
    delayMs?: number;
    loop?: boolean;
    requireVisible?: boolean;
    className?: string;
};

export default function HeroVideoAuto({
                                          src,
                                          poster,
                                          aspectRatio = "16 / 9",
                                          delayMs = 1000,
                                          loop = true,
                                          requireVisible = true,
                                          className,
                                      }: Props) {
    const wrapRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!wrapRef.current) return;
        const saveData = (navigator as any).connection?.saveData === true;
        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (saveData) return;

        let timeoutId: number | undefined;
        let io: IntersectionObserver | undefined;
        let played = false;

        const insertAndPlay = () => {
            if (played || !wrapRef.current) return;
            played = true;

            const v = document.createElement("video");
            v.src = src;
            if (poster) v.poster = poster;
            v.preload = "none";
            v.muted = true;
            v.playsInline = true;
            v.autoplay = true;
            v.loop = !!loop;
            v.controls = false;
            v.style.width = "100%";
            v.style.height = "100%";
            v.style.objectFit = "cover";

            wrapRef.current!.innerHTML = "";
            wrapRef.current!.appendChild(v);

            if (!prefersReducedMotion) {
                v.play().catch(() => {
                    v.controls = true;
                });
            }
        };

        const startAfterLoad = () => {
            timeoutId = window.setTimeout(() => {
                if (!requireVisible) {
                    insertAndPlay();
                    return;
                }
                io = new IntersectionObserver(
                    ([e]) => {
                        if (e.isIntersecting && e.intersectionRatio >= 0.25) {
                            insertAndPlay();
                            io?.disconnect();
                        }
                    },
                    { root: null, threshold: [0.25] }
                );
                if (wrapRef.current) io.observe(wrapRef.current);
            }, delayMs) as unknown as number;
        };

        if (document.readyState === "complete") startAfterLoad();
        else window.addEventListener("load", startAfterLoad, { once: true });

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
            io?.disconnect();
        };
    }, [src, poster, delayMs, loop, requireVisible]);

    return (
        <div
            ref={wrapRef}
            className={className}
            style={{
                width: "100%",
                aspectRatio,
                backgroundImage: poster ? `url(${poster})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                overflow: "hidden",
            }}
            aria-label="Hero video"
        />
    );
}
